// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import { TransporterId } from './TransporterId';
import { VehicleModelId } from './VehicleModelId';
import { MasterVehicleModel } from './MasterVehicleModel';
import { MasterTransporter } from './MasterTransporter';
import { RiderPersonaId } from './RiderPersonaId';
import { RiderPersona } from './MasterRiderPersona';
import { TeamId } from './TeamId';
import { EmailAddress } from './EmailAddress';
import { AuditMetadata } from './AuditMetadata';
import { ExpiryNotification } from './ExpiryNotification';
import { ContractWorkflow } from './ContractWorkflow';

import type { MasterTransporterModelType } from './MasterTransporter';
import type { RiderPersonaIdModelType } from './RiderPersonaId';
import type { RiderPersonaModelType } from './MasterRiderPersona';
import type { TransporterIdModelType } from './TransporterId';
import type { VehicleModelIdModelType } from './VehicleModelId';
import type { MasterVehicleModelModelType } from './MasterVehicleModel';
import type { TeamIdModelType } from './TeamId';
import type { EmailAddressModelType } from 'common/models/flow/EmailAddress';
import type { AuditMetadataModelType } from './AuditMetadata';
import type { ExpiryNotificationModelType } from './Expiration';
import type { ContractWorkflowModelType } from './ContractWorkflow';
import type { ContractCostLookupModelType } from './ContractCostLookup';
import type { ContractCostFunctionModelType } from './ContractCostFunction';

export type StatusEnum = 'ACTIVE' | 'INACTIVE';
export type ContractTypeEnum = 'VEHICLE' | 'RIDER';
export type FleetTypeEnum = 'CAPTIVE' | 'THIRD_PARTY' | 'ADHOC';
// Used for volumeUnit filter options
export const UnitSystemEnum = new EntityTypeEnum({
    METRIC_SYSTEM: 'METRIC_SYSTEM',
    IMPERIAL_SYSTEM: 'IMPERIAL_SYSTEM'
});

export type ContractStatusEnum =
    | 'DRAFT'
    | 'READY'
    | 'VERIFY'
    | 'VOID'
    | 'EXPIRED'
    | 'ACTIVE'
    | 'INACTIVE'
    | 'OPEN'
    | 'PENDING'
    | 'REJECTED'
    | 'VERIFIED'
    | 'REOPEN'
    | 'APPROVED';

export type ContractModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    description: string,
    status: StatusEnum,
    type: ContractTypeEnum,
    fleetType: FleetTypeEnum,
    transporterId: TransporterIdModelType,
    transporter: MasterTransporterModelType,
    vehicleModelIds: Array<VehicleModelIdModelType>,
    vehicleModels: Array<MasterVehicleModelModelType>,
    riderPersonaIds: Array<RiderPersonaIdModelType>,
    riderPersonas: Array<RiderPersonaModelType>,
    teams: Array<TeamIdModelType>,
    currency: string,
    startDate: string,
    expiryDate: string,
    effectiveStatus: ContractStatusEnum,
    emailAddresses: Array<EmailAddressModelType>,
    auditMetadata: AuditMetadataModelType,
    customProperties: { [key: string]: string },
    nextEffectiveStatuses: Array<ContractStatusEnum>,
    version: number,
    costFunctions: Array<ContractCostFunctionModelType>,
    paymentTerms: number,
    contractCostLookup: Array<ContractCostLookupModelType>,
    contractAmount: string,
    expiryNotification: ExpiryNotificationModelType,
    agreementUrls: Array<string>,
    unitSystem: string,
    workflow: ContractWorkflowModelType,
    actions: Array<string>,
    actionDescriptions: Array<string>,
    remarks: Array<string>,
    notifiers: Array<string>,
    allowedEffectiveStatuses: Array<string>
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Contract extends ImmutableModel {
    static fromJS(json: ContractModelType): Contract {
        const state: Object = Object.assign({}, json);
        state.transporterId = TransporterId.fromJS(state.transporterId);
        state.transporter = MasterTransporter.fromJS(state.transporter);
        state.vehicleModelIds = Immutable.List(
            state.vehicleModelIds
        ).map(item => VehicleModelId.fromJS(item));
        state.vehicleModels = Immutable.List(state.vehicleModels).map(item =>
            MasterVehicleModel.fromJS(item)
        );
        state.riderPersonaIds = Immutable.List(
            state.riderPersonaIds
        ).map(item => RiderPersonaId.fromJS(item));
        state.riderPersonas = Immutable.List(state.riderPersonas).map(item =>
            RiderPersona.fromJS(item)
        );
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.emailAddresses = Immutable.List(state.emailAddresses).map(item =>
            EmailAddress.fromJS(item)
        );
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        state.nextEffectiveStatuses = Immutable.List(
            state.nextEffectiveStatuses
        );
        state.costFunctions = Immutable.List(state.costFunctions).map(item =>
            ContractCostFunction.fromJS(item)
        );
        state.contractCostLookup = Immutable.List(
            state.contractCostLookup
        ).map(item => ContractCostLookup.fromJS(item));
        state.expiryNotification = ExpiryNotification.fromJS(
            state.expiryNotification
        );
        state.agreementUrls = Immutable.List(state.agreementUrls);
        state.workflow = ContractWorkflow.fromJS(state.workflow);
        state.actions = Immutable.List(state.actions);
        state.actionDescriptions = Immutable.List(state.actionDescriptions);
        state.remarks = Immutable.List(state.remarks);
        state.notifiers = Immutable.List(state.notifiers);
        state.allowedEffectiveStatuses = Immutable.List(
            state.allowedEffectiveStatuses
        );
        return new this(Immutable.Map(state));
    }

    toJS(): ContractModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            description: this.description,
            status: this.status,
            type: this.type,
            fleetType: this.fleetType,
            transporterId: this.transporterId.toJS(),
            transporter: this.transporter.toJS(),
            vehicleModelIds: this.vehicleModelIds
                .toArray()
                .map(item => item.toJS()),
            vehicleModels: this.vehicleModels
                .toArray()
                .map(item => item.toJS()),
            riderPersonaIds: this.riderPersonaIds
                .toArray()
                .map(item => item.toJS()),
            riderPersonas: this.riderPersonas
                .toArray()
                .map(item => item.toJS()),
            teams: this.teams.toArray().map(item => item.toJS()),
            currency: this.currency,
            startDate: this.startDate,
            expiryDate: this.expiryDate,
            effectiveStatus: this.effectiveStatus,
            emailAddresses: this.emailAddresses
                .toArray()
                .map(item => item.toJS()),
            auditMetadata: this.auditMetadata.toJS(),
            customProperties: this.customProperties.toObject(),
            nextEffectiveStatuses: this.nextEffectiveStatuses.toArray(),
            version: this.version,
            costFunctions: this.costFunctions
                .toArray()
                .map(item => item.toJS()),
            paymentTerms: this.paymentTerms,
            contractCostLookup: this.contractCostLookup
                .toArray()
                .map(item => item.toJS()),
            contractAmount: this.contractAmount,
            expiryNotification: this.expiryNotification.toJS(),
            agreementUrls: this.agreementUrls.toArray(),
            unitSystem: this.unitSystem,
            workflow: this.workflow.toJS(),
            actions: this.actions.toArray(),
            actionDescriptions: this.actionDescriptions.toArray(),
            remarks: this.remarks.toArray(),
            notifiers: this.notifiers.toArray(),
            allowedEffectiveStatuses: this.allowedEffectiveStatuses.toArray()
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get description(): string {
        return this._state.get('description');
    }

    setDescription(description: string): this {
        return this.clone(this._state.set('description', description));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get type(): ContractTypeEnum {
        return this._state.get('type');
    }

    setType(type: ContractTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get fleetType(): FleetTypeEnum {
        return this._state.get('fleetType');
    }

    setFleetType(fleetType: FleetTypeEnum): this {
        return this.clone(this._state.set('fleetType', fleetType));
    }

    get transporterId(): TransporterId {
        return this._state.get('transporterId');
    }

    setTransporterId(transporterId: TransporterId): this {
        return this.clone(this._state.set('transporterId', transporterId));
    }

    get transporter(): MasterTransporter {
        return this._state.get('transporter');
    }

    setTransporter(transporter: MasterTransporter): this {
        return this.clone(this._state.set('transporter', transporter));
    }

    get vehicleModelIds(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModelIds');
    }

    setVehicleModelIds(vehicleModelIds: Immutable.List<VehicleModelId>): this {
        return this.clone(this._state.set('vehicleModelIds', vehicleModelIds));
    }

    get vehicleModels(): Immutable.List<MasterVehicleModel> {
        return this._state.get('vehicleModels');
    }

    setVehicleModels(vehicleModels: Immutable.List<MasterVehicleModel>): this {
        return this.clone(this._state.set('vehicleModels', vehicleModels));
    }

    get riderPersonaIds(): Immutable.List<RiderPersonaId> {
        return this._state.get('riderPersonaIds');
    }

    setRiderPersonaIds(riderPersonaIds: Immutable.List<RiderPersonaId>): this {
        return this.clone(this._state.set('riderPersonaIds', riderPersonaIds));
    }

    get riderPersonas(): Immutable.List<RiderPersona> {
        return this._state.get('riderPersonas');
    }

    setRiderPersonas(riderPersonas: Immutable.List<RiderPersona>): this {
        return this.clone(this._state.set('riderPersonas', riderPersonas));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get currency(): string {
        return this._state.get('currency');
    }

    setCurrency(currency: string): this {
        return this.clone(this._state.set('currency', currency));
    }

    get startDate(): string {
        return this._state.get('startDate');
    }

    setStartDate(startDate: string): this {
        return this.clone(this._state.set('startDate', startDate));
    }

    get expiryDate(): string {
        return this._state.get('expiryDate');
    }

    setExpiryDate(expiryDate: string): this {
        return this.clone(this._state.set('expiryDate', expiryDate));
    }

    get effectiveStatus(): ContractStatusEnum {
        return this._state.get('effectiveStatus');
    }

    setEffectiveStatus(effectiveStatus: ContractStatusEnum): this {
        return this.clone(this._state.set('effectiveStatus', effectiveStatus));
    }

    get emailAddresses(): Immutable.List<EmailAddress> {
        return this._state.get('emailAddresses');
    }

    setEmailAddresses(emailAddresses: Immutable.List<EmailAddress>): this {
        return this.clone(this._state.set('emailAddresses', emailAddresses));
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get nextEffectiveStatuses(): Immutable.List<ContractStatusEnum> {
        return this._state.get('nextEffectiveStatuses');
    }

    setNextEffectiveStatuses(
        nextEffectiveStatuses: Immutable.List<ContractStatusEnum>
    ): this {
        return this.clone(
            this._state.set('nextEffectiveStatuses', nextEffectiveStatuses)
        );
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get costFunctions(): Immutable.List<ContractCostFunction> {
        return this._state.get('costFunctions');
    }

    setCostFunctions(
        costFunctions: Immutable.List<ContractCostFunction>
    ): this {
        return this.clone(this._state.set('costFunctions', costFunctions));
    }

    get paymentTerms(): number {
        return this._state.get('paymentTerms');
    }

    setPaymentTerms(paymentTerms: number): this {
        return this.clone(this._state.set('paymentTerms', paymentTerms));
    }

    get contractCostLookup(): Immutable.List<ContractCostLookup> {
        return this._state.get('contractCostLookup');
    }

    setContractCostLookup(
        contractCostLookup: Immutable.List<ContractCostLookup>
    ): this {
        return this.clone(
            this._state.set('contractCostLookup', contractCostLookup)
        );
    }

    get contractAmount(): string {
        return this._state.get('contractAmount');
    }

    setContractAmount(contractAmount: string): this {
        return this.clone(this._state.set('contractAmount', contractAmount));
    }

    get expiryNotification(): ExpiryNotification {
        return this._state.get('expiryNotification');
    }

    setExpiryNotification(expiryNotification: ExpiryNotification): this {
        return this.clone(
            this._state.set('expiryNotification', expiryNotification)
        );
    }

    get agreementUrls(): Immutable.List<string> {
        return this._state.get('agreementUrls');
    }

    setAgreementUrls(agreementUrls: Immutable.List<string>): this {
        return this.clone(this._state.set('agreementUrls', agreementUrls));
    }

    get unitSystem(): string {
        return this._state.get('unitSystem');
    }

    setUnitSystem(unitSystem: string): this {
        return this.clone(this._state.set('unitSystem', unitSystem));
    }

    get workflow(): ContractWorkflow {
        return this._state.get('workflow');
    }

    setWorkflow(workflow: ContractWorkflow): this {
        return this.clone(this._state.set('workflow', workflow));
    }

    get actions(): Immutable.List<string> {
        return this._state.get('actions');
    }

    setActions(actions: Immutable.List<string>): this {
        return this.clone(this._state.set('actions', actions));
    }

    get actionDescriptions(): Immutable.List<string> {
        return this._state.get('actionDescriptions');
    }

    setActionDescriptions(actionDescriptions: Immutable.List<string>): this {
        return this.clone(
            this._state.set('actionDescriptions', actionDescriptions)
        );
    }

    get remarks(): Immutable.List<string> {
        return this._state.get('remarks');
    }

    setRemarks(remarks: Immutable.List<string>): this {
        return this.clone(this._state.set('remarks', remarks));
    }

    get notifiers(): Immutable.List<string> {
        return this._state.get('notifiers');
    }

    setNotifiers(notifiers: Immutable.List<string>): this {
        return this.clone(this._state.set('notifiers', notifiers));
    }

    get allowedEffectiveStatuses(): Immutable.List<string> {
        return this._state.get('allowedEffectiveStatuses');
    }

    setAllowedEffectiveStatuses(
        allowedEffectiveStatuses: Immutable.List<string>
    ): this {
        return this.clone(
            this._state.set(
                'allowedEffectiveStatuses',
                allowedEffectiveStatuses
            )
        );
    }
}
